import React, { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { BRAND, IMAGES } from "../lib/site";
import { PageHero } from "../components/Sections";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    try {
      setSending(true);
      await axios.post(`${API}/contact`, form);
      const subject = encodeURIComponent(form.subject || "Inquiry via medicalartsgrove.online");
      const body = encodeURIComponent(
        `Name: ${form.name}\nEmail: ${form.email}\n\nMessage:\n${form.message}`
      );
      window.location.href = `mailto:${BRAND.email}?subject=${subject}&body=${body}`;
      toast.success("Message saved. Opening your email to confirm…");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Unable to send. Please email " + BRAND.email);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <PageHero
        kicker="Visit & Connect"
        title="Step into the"
        accent="atelier"
        subtitle="We welcome inquiries by phone, email, or this private form. Our concierge responds within one business day."
        image={IMAGES.abstractGold}
        testid="contact-hero"
      />

      <section className="py-20 md:py-28 bg-mag">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <h3 className="font-heading text-4xl text-white">Reach the House</h3>
            <p className="text-white/65 mt-4 font-heading text-lg leading-relaxed">
              For consultation reservations, recovery suite inquiries, or
              press &amp; partnership, please use the channels below.
            </p>

            <div className="mt-10 space-y-7">
              {BRAND.offices.map((o, i) => (
                <div className="flex gap-4 items-start" key={i} data-testid={`contact-office-${i}`}>
                  <MapPin className="text-gold mt-1 shrink-0" size={20} />
                  <div>
                    <p className="text-[11px] tracking-[0.3em] uppercase text-pink-luxe">{o.label}</p>
                    <p className="text-white/85 mt-1 font-heading text-lg leading-snug">
                      {o.line1}<br />
                      {o.line2}
                    </p>
                  </div>
                </div>
              ))}

              <div className="flex gap-4 items-start" data-testid="contact-phone-block">
                <Phone className="text-gold mt-1 shrink-0" size={20} />
                <div>
                  <p className="text-[11px] tracking-[0.3em] uppercase text-pink-luxe">Phone</p>
                  <a href={`tel:+1${BRAND.phoneHref}`} className="text-white/85 mt-1 font-heading text-lg block hover:text-gold">
                    {BRAND.phone}
                  </a>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-testid="contact-email-block">
                <Mail className="text-gold mt-1 shrink-0" size={20} />
                <div>
                  <p className="text-[11px] tracking-[0.3em] uppercase text-pink-luxe">Email</p>
                  <a href={`mailto:${BRAND.email}`} className="text-white/85 mt-1 font-heading text-lg block hover:text-gold break-all">
                    {BRAND.email}
                  </a>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-testid="contact-hours-block">
                <Clock className="text-gold mt-1 shrink-0" size={20} />
                <div>
                  <p className="text-[11px] tracking-[0.3em] uppercase text-pink-luxe">Hours of Operation</p>
                  <div className="mt-1 space-y-1">
                    {BRAND.hours.map((h, i) => (
                      <p className="text-white/85 font-heading text-lg" key={i}>
                        <span className="text-gold">{h.days}</span> · {h.time}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="mag-card p-8 md:p-10 space-y-5"
            >
              <h3 className="font-heading text-3xl text-white">Send a Message</h3>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-[11px] tracking-[0.3em] uppercase text-gold">Name *</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    required
                    data-testid="contact-input-name"
                    className="mag-input mt-2"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="text-[11px] tracking-[0.3em] uppercase text-gold">Email *</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    data-testid="contact-input-email"
                    className="mag-input mt-2"
                    placeholder="you@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] tracking-[0.3em] uppercase text-gold">Subject</label>
                <input
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  data-testid="contact-input-subject"
                  className="mag-input mt-2"
                  placeholder="How can we help?"
                />
              </div>

              <div>
                <label className="text-[11px] tracking-[0.3em] uppercase text-gold">Message *</label>
                <textarea
                  name="message"
                  rows={6}
                  value={form.message}
                  onChange={onChange}
                  required
                  data-testid="contact-input-message"
                  className="mag-input mt-2"
                  placeholder="Tell us a little more…"
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                className="btn-gold w-full justify-center disabled:opacity-60"
                data-testid="contact-submit-btn"
              >
                {sending ? "Sending..." : (<>Send Message <Send size={14} /></>)}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
